import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { compose } from 'ramda';
import styled from 'styled-components/macro';
import Grid from '@material-ui/core/Grid';
import { FormikHelpers } from 'formik';
import {
  showToast,
  Button,
  BackTo,
  Typography,
  Tables,
} from '@deeptrue-frontend/ui';
import Forms from 'components/Forms';
import { Dashboard } from 'components/Layout';
import StoreProvider from 'components/StoreProvider/StoreProvider';
import { ProjectType } from 'store/modules/projects/projects.types';
import { OrganizationType } from 'store/modules/auth/auth';
import { withMetaDescription } from 'components/HeadProvider/HeadProvider';
import api from 'api/api';
import ROUTES from 'consts/routing';
import { useFormatMessage } from 'components/IntlProvider';
import { withProject, WithProjectProps } from '../../withProject';
import { FormValues } from 'components/Forms/AutomaticDataCollect';
import { translateFieldErrors } from 'components/IntlProvider/translateFieldErrors';

const { connect, selectors, actions } = StoreProvider;
const { TableHeader, Table, TableBody, TableCell, TableRow } = Tables;

const Tokens = styled(Typography)`
  color: ${({ theme }) => theme.colors.colorPrimary};
  margin-top: 2rem;
`;

const Error = styled(Typography)`
  color: ${({ theme }) => theme.colors.scaryRed};
`;

const StyledCell = styled(TableCell)<{ isErr?: boolean }>`
  ${({ theme, isErr }) =>
    isErr &&
    `

    color: ${theme.colors.scaryRed}
  `}
`;
const TableContainer = styled.div`
  background-color: rgba(31, 93, 234, 0.02);
  height: 100%;
  padding: 5rem 3rem;
`;

const Content = styled(Grid)`
  padding: 0 12px !important;
`;

const Theader = styled(TableHeader)`
  th {
    border: 0;
  }
`;

const BtnBox = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const TitleBoxLeftColumnt = styled.div`
  margin: 3rem 0 2rem 0;
`;

const TitleBox = styled.div`
  width: 100%;
  margin: 1rem 0 2rem 0;
`;

const Title = styled(Typography)`
  font-weight: 500;
  line-height: 2rem;
  margin-bottom: 1rem;
`;

const Paragraph = styled(Typography)`
  margin-bottom: 2rem;
`;

const BackToBox = styled.div`
  padding-top: 0.5rem;
`;

interface Props extends RouteComponentProps<{ id: string; codename: string }> {
  project: ProjectType;
  updateProject: (project: WithProjectProps['project']) => void;
  organization: OrganizationType;
}

interface LimitData {
  limit: number;
  available_limit: number;
  reserved_tokens: number;
  available_tokens: number;
}

const AutomaticDataCollect = ({
  project,
  organization,
  location,
  history,
  updateProject,
  match: { params },
}: Props) => {
  const [limitsRes, setLimitsRes] = useState<LimitData>({
    limit: 0,
    available_limit: 0,
    reserved_tokens: 0,
    available_tokens: 0,
  });
  const t = useFormatMessage();
  const { name, final_fee, json } = project;

  const { Bsample_size } = json;

  useEffect(() => {
    api.resources
      .getOrganizationPaymentLimits(params.codename || organization.codename)
      .then(({ data }) => {
        setLimitsRes(data);
      })
      .catch(() => {
        showToast({
          type: 'error',
          title: t(
            'errors.toast.general',
            'Something went wrong, please contact administrator'
          ),
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.codename]);

  const startDataCollection = (
    _values: FormValues,
    { setErrors, setSubmitting }: FormikHelpers<FormValues>
  ) => {
    api.resources
      .collectProjectData({
        organizationCodename: params.codename || organization.codename,
        id: project.id,
      })
      .then(({ data }) => {
        updateProject(data);
        if (params.codename) {
          return history.push(
            `/admin/organizations/${params.codename}/projects/${data.id}/statistics`
          );
        }
        history.push(`/project/${data.id}/statistics`);
      })
      .catch((err) => {
        setSubmitting(false);
        setErrors(
          translateFieldErrors({
            t,
            err: err.response.data,
          })
        );
      });
  };

  const isProjectExceedBudget = final_fee > limitsRes.available_tokens;
  const balanceAfterPay = limitsRes.available_tokens - final_fee;
  const backLink = params.codename
    ? `/admin/organizations/${params.codename}/projects/${project.id}/collect-data`
    : `/project/${project.id}/collect-data`;

  return (
    <Dashboard
      title={name || t('page.project', 'Project details')}
      location={location}
    >
      <Grid container spacing={4}>
        <Content item xs={12} md={6}>
          <BackToBox>
            <BackTo onClick={() => history.push(backLink)}>
              {t('project.collectData.backTo', 'Back to collect data')}
            </BackTo>
          </BackToBox>
          <Grid item xs={12}>
            <TitleBoxLeftColumnt>
              <Title variant="h5">
                {t(
                  'project.collectData.automatic.title',
                  'Start Data Collection for my insigh project'
                )}
                {` ${name}`}
              </Title>
            </TitleBoxLeftColumnt>
          </Grid>
          <Paragraph variant="copy1">
            {t(
              'project.collectData.automatic.p1',
              'Deeptrue will run the data collection according to the briefing you have completed.'
            )}
          </Paragraph>
          <Paragraph variant="copy1">
            {t(
              'project.collectData.automatic.p2-1',
              'The cost of collecting the data from'
            )}
            <strong>{` ${Bsample_size || 0} `}</strong>
            {t('project.collectData.automatic.p2-2', 'consumers is')}
            <strong>
              {` ${final_fee || 0} `}
              {t('project.collectData.automatic.p2-3', 'credits. ')}
            </strong>

            {t(
              'project.collectData.automatic.p2-4',
              'The cost will be deducted from your balance of credits.'
            )}
          </Paragraph>
          <Paragraph variant="copy1">
            {t(
              'project.collectData.automatic.p3-1',
              'By starting data collection you sign a contract with deeptrue, commissioning project execution as defined'
            )}
            <br />
            <a
              href={t(
                'project.collectData.linkModal.termsAndConditions.link',
                'https://deeptrue.com/de/terms-and-conditions'
              )}
              target="_blank"
              rel="noopener noreferrer"
            >
              <strong>{t('project.collectData.automatic.p3-2', 'here')}</strong>
            </a>
          </Paragraph>

          <Paragraph variant="copy1">
            {t(
              'project.collectData.automatic.p4',
              'In 3 - 4 days you will find a complete report on your dashboard.'
            )}
          </Paragraph>
          <Forms.AutomaticDataCollect
            startDataCollection={startDataCollection}
            totalFee={final_fee}
            limits={limitsRes.available_tokens}
            voucherInfo={project.voucher}
            initialValues={{
              termsAndConditions: false,
              understandTerms: false,
            }}
          />
          <Tokens variant="copy1">
            {t(
              'project.collectDataa.automatic.tokens',
              '{tokens} credits available',
              { tokens: limitsRes.available_tokens }
            )}
          </Tokens>
        </Content>
        <Content item xs={12} md={6}>
          <TableContainer>
            <TitleBox>
              <Title variant="h5">
                {t('project.collectData.automatic.credit', 'Buy Credits')}
              </Title>
            </TitleBox>
            <Table>
              <Theader
                cells={[
                  t(
                    'project.collectData.automatic.table.balance',
                    'Your balance of credits'
                  ),
                  limitsRes.available_tokens,
                ]}
              />
              <TableBody>
                <TableRow>
                  <TableCell>
                    {t(
                      'project.collectData.automatic.table.costs',
                      'Project Costs'
                    )}
                  </TableCell>
                  <TableCell>{final_fee}</TableCell>
                </TableRow>
                <TableRow>
                  <StyledCell isErr={isProjectExceedBudget}>
                    {t(
                      'project.collectData.automatic.table.balance',
                      'Balance after project'
                    )}
                  </StyledCell>
                  <StyledCell isErr={isProjectExceedBudget}>
                    {balanceAfterPay || ''}
                  </StyledCell>
                </TableRow>
              </TableBody>
            </Table>
            {isProjectExceedBudget && (
              <Error variant="copy2">
                *
                {t(
                  'project.collectData.automatic.table.balance.exceed',
                  'You dont have enough credits'
                )}
              </Error>
            )}
            <BtnBox>
              <Button
                onClick={() => history.push(ROUTES.PAYMENTS.MAIN)}
                variant="primary"
              >
                {t('project.collectData.automatic.buyCredit', 'Buy credits')}
              </Button>
            </BtnBox>
          </TableContainer>
        </Content>
      </Grid>
    </Dashboard>
  );
};

export default compose(
  connect(
    (state) => ({
      organization: selectors.authSelectors.userOrganization(state),
    }),
    {
      updateProject: actions.projects.setProject,
    }
  ),
  withMetaDescription({ title: 'Dashboard projects' }),
  withProject(({ match, organization }: Props) => ({
    projectId: match.params.id,
    organizationCodename: match.params.codename || organization.codename,
  }))
)(AutomaticDataCollect);
